import { CSSProperties, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { ButtonType, Size } from 'coreOld/enums';

interface ButtonWrapperProps {
  fullWidth?: boolean;
  fullHeight?: boolean;
  hasMarginRight?: boolean;
  size?: Size;
  btnType?: ButtonType;
  disabled?: boolean;
  style?: CSSProperties;
  children?: ReactNode;
  id?: string;
  onClick?: any;
  onMouseDown?: any;
  type?: string;
}

const XS_BUTTON = css`
  margin-left: 8px;
  font-size: 12px;
  font-weight: 700;
  padding: 4px;
  width: 60px;
  height: 27px;
`;

const SM_BUTTON = css`
  margin-left: 8px;
  font-size: 14px;
  padding: 8px 20px;
  width: fit-content;
`;

const MD_BUTTON = css`
  margin-left: 8px;
  font-size: 16px;
  padding: 4px;
  height: 32px;
  min-width: 32px;
  width: fit-content;
`;

const getCustomButtonStyle = (type: ButtonType, inverted = false) => {
  const colorLabel = [type, 'Btn'].join('');

  if (ButtonType.ACTION === type && inverted) {
    return css`
      background-color: ${({ theme }) => theme.palette.backgrounds.main};
      color: ${({ theme }) => theme.colors[colorLabel]};
      border: ${({ theme }) => `1px solid ${theme.colors[colorLabel]}`};

      &:hover,
      &:focus {
        background-color: ${({ theme }) => theme.colors[colorLabel]};
        color: ${({ theme }) => theme.palette.text.secondary};
      }
    `;
  }

  if (ButtonType.CANCEL === type && inverted) {
    return css`
      background-color: ${({ theme }) => theme.palette.backgrounds.main};
      color: ${({ theme }) => theme.palette.text.button};
      border: ${({ theme }) => `1px solid ${theme.palette.text.button}`};

      &:hover,
      &:focus {
        background-color: ${({ theme }) => theme.palette.text.button};
        border-color: ${({ theme }) => theme.palette.text.button};
        color: ${({ theme }) => theme.palette.text.secondary};
      }
    `;
  }

  return css`
    background-color: ${({ theme }) => theme.colors[colorLabel]};
    border: ${({ theme }) => `1px solid ${theme.colors[colorLabel]}`};
    color: ${({ theme }) => theme.palette.text.secondary};

    &:hover,
    &:focus {
      background-color: ${({ theme }) => theme.palette.backgrounds.main};
      color: ${({ theme }) => theme.colors[colorLabel]};
    }
  `;
};

const sizeButtonStyle = {
  [Size.XS]: XS_BUTTON,
  [Size.SM]: SM_BUTTON,
  [Size.MD]: MD_BUTTON,
};

export const typeButtonStyle = {
  [ButtonType.ACTION]: getCustomButtonStyle(ButtonType.ACTION),
  [ButtonType.ACTION_INVERTED]: getCustomButtonStyle(ButtonType.ACTION, true),
  [ButtonType.CANCEL]: getCustomButtonStyle(ButtonType.CANCEL),
  [ButtonType.CANCEL_INVERTED]: getCustomButtonStyle(ButtonType.CANCEL, true),
  [ButtonType.DANGER]: getCustomButtonStyle(ButtonType.DANGER),
  [ButtonType.INLINE]: getCustomButtonStyle(ButtonType.INLINE),
  [ButtonType.OPTION]: getCustomButtonStyle(ButtonType.OPTION),
  [ButtonType.SUCCESS]: getCustomButtonStyle(ButtonType.SUCCESS),
};

export const ButtonWrapper = styled.button<ButtonWrapperProps>`
  border-radius: 5px;
  color: ${({ theme }) => theme.palette.text.secondary};
  display: inline-block;
  padding: 8px 30px;
  text-align: center;
  font:
    400 16px 'Open Sans',
    sans-serif;
  white-space: nowrap;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s linear;

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}

  ${({ fullHeight }) =>
    fullHeight &&
    css`
      height: 100%;
    `}

  ${({ hasMarginRight }) =>
    hasMarginRight &&
    css`
      margin-right: 8px;
    `}

  ${({ size }) =>
    size &&
    css`
      ${sizeButtonStyle[size]}
    `}

  ${({ btnType }) =>
    btnType &&
    css`
      ${typeButtonStyle[btnType]}
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.65;
      pointer-events: none;
    `}

  ${({ style }) => ({ ...style })}
`;
